import { normalizarSku, type FilaCatalogo } from '@tanteo/compartido';
import { ErrorDeApi } from './error-api';
import { listarImpuestos } from './api-cuentas';
import { supabase } from './supabase';

export interface ProductoEntrada {
  sku: string;
  ean: string | null;
  nombre: string;
  categoria: string;
  precio: number;
  /** Los impuestos del catálogo que aplican; vacío = exento. */
  impuestoIds: string[];
  activo: boolean;
}

/** Deja la tabla de unión con exactamente los impuestos marcados. */
async function fijarImpuestos(negocioId: string, productoId: string, impuestoIds: string[]): Promise<void> {
  const { error: eBorrar, status: statusBorrar } = await supabase
    .from('producto_impuestos')
    .delete()
    .eq('producto_id', productoId)
    .not('impuesto_id', 'in', `(${impuestoIds.length ? impuestoIds.join(',') : '00000000-0000-0000-0000-000000000000'})`);
  if (eBorrar) throw new ErrorDeApi(eBorrar, statusBorrar);
  if (impuestoIds.length === 0) return;
  const { error, status } = await supabase.from('producto_impuestos').upsert(
    impuestoIds.map((impuesto_id) => ({ negocio_id: negocioId, producto_id: productoId, impuesto_id })),
    { onConflict: 'producto_id,impuesto_id' }
  );
  if (error) throw new ErrorDeApi(error, status);
}

/** El 23505 puede venir del candado del SKU o del código de barras: el mensaje dice cuál. */
function mensajeDuplicado(e: { message?: string; details?: string }): string {
  return `${e.message ?? ''} ${e.details ?? ''}`.includes('producto_ean_unico')
    ? 'Ya existe un producto con ese código de barras.'
    : 'Ya existe un producto con ese SKU.';
}

export async function crearProducto(negocioId: string, p: ProductoEntrada): Promise<void> {
  const { data, error } = await supabase
    .from('productos')
    .insert({
      negocio_id: negocioId,
      sku: normalizarSku(p.sku),
      ean: p.ean || null,
      nombre: p.nombre.trim(),
      categoria: p.categoria.trim(),
      precio: p.precio,
      es_activo: p.activo,
    })
    .select('id')
    .single();
  if (error) throw new Error(error.code === '23505' ? mensajeDuplicado(error) : error.message);
  await fijarImpuestos(negocioId, data.id, p.impuestoIds);
}

export async function actualizarProducto(negocioId: string, id: string, p: Partial<ProductoEntrada>): Promise<void> {
  const cambios: Record<string, unknown> = {};
  if (p.sku !== undefined) cambios.sku = normalizarSku(p.sku);
  if (p.ean !== undefined) cambios.ean = p.ean || null;
  if (p.nombre !== undefined) cambios.nombre = p.nombre.trim();
  if (p.categoria !== undefined) cambios.categoria = p.categoria.trim();
  if (p.precio !== undefined) cambios.precio = p.precio;
  if (p.activo !== undefined) cambios.es_activo = p.activo;
  const { error } = await supabase.from('productos').update(cambios).eq('negocio_id', negocioId).eq('id', id);
  if (error) throw new Error(error.code === '23505' ? mensajeDuplicado(error) : error.message);
  if (p.impuestoIds !== undefined) await fijarImpuestos(negocioId, id, p.impuestoIds);
}

/** Borrado suave por RPC: solo un producto inactivo; sale de todas las pantallas y el SKU queda libre. */
export async function suprimirProductoRpc(productoId: string): Promise<void> {
  const { error, status } = await supabase.rpc('suprimir_producto', { p_producto: productoId });
  if (error) throw new ErrorDeApi(error, status);
}

export interface Tercero {
  id: string;
  tipoDocumento: string;
  numeroDocumento: string;
  nombre: string;
  telefono: string | null;
  correo: string | null;
  esCliente: boolean;
  esProveedor: boolean;
  origen: string;
  consentimiento: string | null;
}

export async function listarTerceros(negocioId: string): Promise<Tercero[]> {
  const { data, error, status } = await supabase
    .from('terceros')
    .select('id, documento_tipo, documento_numero, nombre, telefono, correo, es_cliente, es_proveedor, origen, consentimiento_datos_en')
    .eq('negocio_id', negocioId)
    .is('eliminado_en', null)
    .order('nombre');
  if (error) throw new ErrorDeApi(error, status);
  return (data ?? []).map((t) => ({
    id: t.id,
    tipoDocumento: t.documento_tipo,
    numeroDocumento: t.documento_numero,
    nombre: t.nombre,
    telefono: t.telefono,
    correo: t.correo,
    esCliente: t.es_cliente,
    esProveedor: t.es_proveedor,
    origen: t.origen,
    consentimiento: t.consentimiento_datos_en,
  }));
}

interface TerceroEntrada {
  tipoDocumento: string;
  numeroDocumento: string;
  nombres: string;
  apellidos?: string;
  razonSocial?: string;
  telefono?: string;
  correo?: string;
  esCliente: boolean;
  esProveedor: boolean;
  autorizaDatos: boolean;
}

/** El alta pasa por el RPC: valida el documento, normaliza el celular y sella la autorización. */
export async function crearTercero(negocioId: string, t: TerceroEntrada): Promise<string> {
  const { data, error } = await supabase.rpc('crear_tercero', {
    p_negocio: negocioId,
    p_tipo_documento: t.tipoDocumento,
    p_numero_documento: t.numeroDocumento,
    p_nombres: t.nombres,
    p_apellidos: t.apellidos ?? null,
    p_razon_social: t.razonSocial ?? null,
    p_telefono: t.telefono ?? null,
    p_correo: t.correo ?? null,
    p_es_cliente: t.esCliente,
    p_es_proveedor: t.esProveedor,
    p_autoriza_datos: t.autorizaDatos,
  });
  if (error) throw new Error(error.code === '23505' ? 'Ya existe un tercero con ese documento.' : error.message);
  return data as string;
}

export async function suprimirTerceroRpc(terceroId: string): Promise<void> {
  const { error, status } = await supabase.rpc('suprimir_tercero', { p_tercero: terceroId });
  if (error) throw new ErrorDeApi(error, status);
}

interface ResultadoImportacion {
  importados: number;
  errores: string[];
}

/**
 * Importación masiva desde CSV; la escritura vive en `importar_catalogo`, en una transacción.
 * El chequeo de impuestos de aquí es un aviso previo: la regla que manda es la del servidor.
 */
export async function importarProductos(negocioId: string, filas: FilaCatalogo[]): Promise<ResultadoImportacion> {
  if (filas.length === 0) return { importados: 0, errores: [] };

  const impuestos = await listarImpuestos(negocioId);
  const conocidos = new Set(impuestos.map((i) => i.nombre.trim().toLowerCase()));
  const desconocidos = [
    ...new Set(filas.flatMap((f) => f.impuestos).filter((n) => !conocidos.has(n.trim().toLowerCase()))),
  ];
  if (desconocidos.length > 0) {
    return {
      importados: 0,
      errores: [
        `Estos impuestos no existen en tu configuración: ${desconocidos.join(', ')}. ` +
          'Créalos en Configuración → Impuestos, o corrige el archivo. No se importó nada.',
      ],
    };
  }

  const { data, error, status } = await supabase.rpc('importar_catalogo', {
    p_negocio: negocioId,
    p_filas: filas,
  });
  if (error) throw new ErrorDeApi(error, status);
  return { importados: Number(data ?? 0), errores: [] };
}

export interface ClienteBusqueda {
  id: string;
  nombre: string;
  numeroDocumento: string;
}

export interface TerceroEncontrado {
  id: string;
  nombre: string;
  tipoDocumento: string;
  numeroDocumento: string;
}

/**
 * El tercero de un documento exacto, sin filtrar por papel: si el mismo NIT ya está como cliente, es
 * el mismo tercero y el gasto le suma el de proveedor. Devuelve null si no hay ninguno.
 */
export async function terceroPorDocumento(
  negocioId: string,
  tipoDocumento: string,
  numeroDocumento: string
): Promise<TerceroEncontrado | null> {
  const numero = numeroDocumento.trim();
  // El DV viaja aparte en la tabla, así que se busca por el número sin verificador.
  const sinDv = tipoDocumento === '31' ? numero.split('-')[0]! : numero;
  if (!sinDv) return null;
  const { data, error, status } = await supabase
    .from('terceros')
    .select('id, nombre, documento_tipo, documento_numero')
    .eq('negocio_id', negocioId)
    .eq('documento_tipo', tipoDocumento)
    .eq('documento_numero', sinDv)
    .is('eliminado_en', null)
    .maybeSingle();
  if (error) throw new ErrorDeApi(error, status);
  if (!data) return null;
  return {
    id: data.id,
    nombre: data.nombre,
    tipoDocumento: data.documento_tipo,
    numeroDocumento: data.documento_numero,
  };
}

/** Proveedores del negocio, para elegir uno al registrar un gasto. */
export async function buscarProveedores(negocioId: string, texto: string): Promise<TerceroEncontrado[]> {
  const q = texto.trim();
  let consulta = supabase
    .from('terceros')
    .select('id, nombre, documento_tipo, documento_numero')
    .eq('negocio_id', negocioId)
    .eq('es_proveedor', true)
    .is('eliminado_en', null)
    .order('nombre')
    .limit(20);
  if (q) consulta = consulta.or(`nombre.ilike.%${q}%,documento_numero.ilike.%${q}%`);
  const { data, error, status } = await consulta;
  if (error) throw new ErrorDeApi(error, status);
  return (data ?? []).map((t) => ({
    id: t.id,
    nombre: t.nombre,
    tipoDocumento: t.documento_tipo,
    numeroDocumento: t.documento_numero,
  }));
}

/** Clientes del negocio para asociar a una venta (excluye el consumidor final genérico). */
export async function buscarClientes(negocioId: string, texto: string): Promise<ClienteBusqueda[]> {
  const q = texto.trim();
  let consulta = supabase
    .from('terceros')
    .select('id, nombre, documento_numero')
    .eq('negocio_id', negocioId)
    .eq('es_cliente', true)
    .neq('documento_numero', '222222222222')
    .is('eliminado_en', null)
    .order('nombre')
    .limit(20);
  if (q) consulta = consulta.or(`nombre.ilike.%${q}%,documento_numero.ilike.%${q}%`);
  const { data, error, status } = await consulta;
  if (error) throw new ErrorDeApi(error, status);
  return (data ?? []).map((t) => ({ id: t.id, nombre: t.nombre, numeroDocumento: t.documento_numero }));
}
